import { useQuery } from '@tanstack/react-query';
import { Sparkles, TrendingUp, Calendar, Target } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { formatChf } from '@/lib/format';

interface NetWorthResponse {
  netWorth: number;
  totalAssets: number;
  totalLiabilities: number;
  pctOfMillion: number;
  millionGap: number;
  target: number;
  asOfDate: string;
  byAccount: Array<{ accountId: string; name: string; type: string; valueChf: number; ledger: string }>;
  projection: {
    currentNetWorth: number;
    monthlyVelocity: number;
    monthsToMillion: number | null;
    projectedDate: string | null;
    yearsToMillion: number | null;
    sensitivity: Array<{ extraMonthly: number; monthsSaved: number; newDate: string | null }>;
  };
}

async function fetchNetWorth(): Promise<NetWorthResponse> {
  const res = await fetch('/api/net-worth', { credentials: 'include' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function formatProjectedDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m] = iso.split('-');
  const months = ['janv.', 'févr.', 'mars', 'avril', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
  const monthIdx = m ? Number(m) - 1 : 0;
  return `${months[monthIdx] ?? ''} ${y ?? ''}`.trim();
}

export function MillionHero() {
  const { data, isLoading } = useQuery({ queryKey: ['net-worth'], queryFn: fetchNetWorth });

  if (isLoading) {
    return (
      <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5">
        <CardContent className="p-6 sm:p-8">
          <div className="text-sm text-muted-foreground">Calcul de la trajectoire…</div>
        </CardContent>
      </Card>
    );
  }
  if (!data) return null;

  const pct = Math.max(0, Math.min(100, data.pctOfMillion * 100));
  const yearsLeft = data.projection.yearsToMillion;

  return (
    <Card className="overflow-hidden border-2 border-primary/30 bg-gradient-to-br from-primary/10 via-background to-accent/5 shadow-lg">
      <CardContent className="p-6 sm:p-8">
        <div className="mb-1 flex items-center gap-2 text-xs uppercase tracking-wider text-primary">
          <Sparkles className="h-3.5 w-3.5" />
          Trajectoire vers le million
        </div>

        <div className="mt-2 flex flex-wrap items-baseline gap-2 sm:gap-4">
          <div className="text-4xl font-bold num-tnum sm:text-5xl">
            {formatChf(data.netWorth, { decimals: 0 })}
          </div>
          <div className="text-base text-muted-foreground sm:text-lg">
            / {formatChf(data.target, { decimals: 0 })}
          </div>
          <div className="ml-auto rounded-full bg-primary/15 px-3 py-1 text-sm font-semibold text-primary">
            {pct.toFixed(2)} %
          </div>
        </div>

        <div className="mt-4">
          <Progress value={pct} indicatorClassName="bg-gradient-to-r from-primary to-accent" />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Stat
            icon={<Target className="h-4 w-4" />}
            label="Reste à atteindre"
            value={formatChf(data.millionGap, { decimals: 0 })}
            sub=""
          />
          <Stat
            icon={<TrendingUp className="h-4 w-4" />}
            label="Vélocité (6 mois)"
            value={
              data.projection.monthlyVelocity > 0
                ? `${formatChf(data.projection.monthlyVelocity, { decimals: 0 })} / mois`
                : '—'
            }
            sub={data.projection.monthlyVelocity <= 0 ? 'Importe ton historique pour calculer' : ''}
          />
          <Stat
            icon={<Calendar className="h-4 w-4" />}
            label="Date projetée"
            value={formatProjectedDate(data.projection.projectedDate)}
            sub={
              yearsLeft !== null
                ? `dans ${yearsLeft.toFixed(1)} ans`
                : data.projection.monthlyVelocity <= 0
                  ? 'vélocité requise'
                  : ''
            }
          />
        </div>

        {data.projection.sensitivity.some((s) => s.monthsSaved > 0) ? (
          <div className="mt-6 rounded-lg border border-border/50 bg-card/50 p-4">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Sensibilité — accélérer vers le million
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              {data.projection.sensitivity.map((s) => (
                <div key={s.extraMonthly} className="rounded-md bg-background p-2">
                  <div className="font-semibold">+ {formatChf(s.extraMonthly, { decimals: 0 })}/mois</div>
                  <div className="text-xs text-success">
                    {s.monthsSaved > 0 ? `− ${s.monthsSaved} mois` : '—'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {data.byAccount.length === 0 ? (
          <div className="mt-4 rounded-lg border border-dashed border-border bg-card/50 p-4 text-center text-sm text-muted-foreground">
            Importe ton 1er extrait bancaire pour démarrer la trajectoire →{' '}
            <a href="/import" className="font-medium text-primary hover:underline">
              Page Import
            </a>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

function Stat({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub: string }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold num-tnum">{value}</div>
      {sub ? <div className="text-xs text-muted-foreground">{sub}</div> : null}
    </div>
  );
}
